/**
 * Sysadmin Platform — Overview (Wave 1a / W1a-16).
 *
 * Mounted at `/sysadmin`. Cross-tenant operational dashboard:
 * tenant breakdown, user breakdown, sysadmin counts, storage totals,
 * platform alerts, and the most recent 20 platform-audit rows.
 *
 * Backed by `GET /api/v1/sysadmin/overview`. The hook auto-refreshes
 * every 30 s so the page feels live (each refetch writes a
 * PLATFORM_OVERVIEW_READ audit row — kept low-frequency on purpose).
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import {
  LayoutDashboard,
  ShieldCheck,
  Building2,
  Users,
  HardDrive,
  AlertTriangle,
  Info,
  AlertCircle,
  ArrowRight,
  Clock,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  usePlatformOverview,
  type PlatformAlert,
} from '@/lib/use-platform-overview';

export const Route = createFileRoute('/sysadmin/')({
  component: PlatformOverviewPage,
});

// ─── Page ────────────────────────────────────────────────────────────────────

function PlatformOverviewPage() {
  const { data, isLoading, error, dataUpdatedAt, isFetching } =
    usePlatformOverview();

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <LayoutDashboard className="h-6 w-6" />
            Platform Overview
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Cross-tenant operations control plane.
          </p>
        </div>
        <div className="text-xs text-muted-foreground inline-flex items-center gap-2">
          <Clock className="h-3 w-3" />
          {isLoading
            ? 'Loading…'
            : dataUpdatedAt
              ? `Updated ${formatRelative(new Date(dataUpdatedAt))}`
              : '—'}
          {isFetching && !isLoading && (
            <span className="text-primary"> • refreshing</span>
          )}
        </div>
      </div>

      {error && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="py-4 text-sm text-destructive">
            Failed to load platform overview: {error.message}
          </CardContent>
        </Card>
      )}

      {/* ── Alert strip ─────────────────────────────────────────────────── */}
      {data?.alerts && data.alerts.length > 0 && (
        <div className="space-y-2">
          {data.alerts.map((alert) => (
            <AlertBanner key={alert.code} alert={alert} />
          ))}
        </div>
      )}

      {/* ── Tenant stats row ────────────────────────────────────────────── */}
      <section>
        <SectionHeading
          icon={<Building2 className="h-4 w-4" />}
          title="Tenants"
          subtitle={data ? `${data.tenants.total} total` : undefined}
          link={{ to: '/sysadmin/tenants', label: 'Manage' }}
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <StatCard
            label="Active"
            value={data?.tenants.active}
            loading={isLoading}
            tone="default"
          />
          <StatCard
            label="Pending"
            value={data?.tenants.pending}
            loading={isLoading}
            tone="warning"
            hint="Tenants with zero users"
          />
          <StatCard
            label="Suspended"
            value={data?.tenants.suspended}
            loading={isLoading}
            tone="muted"
          />
          <StatCard
            label="Soft-deleted"
            value={data?.tenants.deleted}
            loading={isLoading}
            tone="destructive"
            hint="Within 60-day purge window"
          />
        </div>
      </section>

      {/* ── User stats row ─────────────────────────────────────────────── */}
      <section>
        <SectionHeading
          icon={<Users className="h-4 w-4" />}
          title="Users"
          subtitle={data ? `${data.users.total} total across all tenants` : undefined}
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <StatCard
            label="Total"
            value={data?.users.total}
            loading={isLoading}
          />
          <StatCard
            label="Active"
            value={data?.users.active}
            loading={isLoading}
            tone="default"
          />
          <StatCard
            label="Invited"
            value={data?.users.invited}
            loading={isLoading}
            tone="muted"
          />
          <StatCard
            label="Suspended"
            value={data?.users.suspended}
            loading={isLoading}
            tone="warning"
          />
        </div>
      </section>

      {/* ── Sysadmin stats row ─────────────────────────────────────────── */}
      <section>
        <SectionHeading
          icon={<ShieldCheck className="h-4 w-4" />}
          title="Platform administrators"
          link={{ to: '/sysadmin/admins', label: 'Manage' }}
        />
        <div className="grid grid-cols-2 md:grid-cols-2 gap-3 mt-3 max-w-md">
          <StatCard
            label="Active sysadmins"
            value={data?.sysadmins.active}
            loading={isLoading}
            tone="default"
          />
          <StatCard
            label="Inactive"
            value={data?.sysadmins.inactive}
            loading={isLoading}
            tone="muted"
          />
        </div>
      </section>

      {/* ── Storage row ────────────────────────────────────────────────── */}
      <section>
        <SectionHeading
          icon={<HardDrive className="h-4 w-4" />}
          title="Storage"
          subtitle="Aggregated across all tenants"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <StatCard
            label="Documents"
            value={data?.storage.documentsCount}
            loading={isLoading}
          />
          <StatCard
            label="Conversations"
            value={data?.storage.conversationsCount}
            loading={isLoading}
          />
          <StatCard
            label="Messages"
            value={data?.storage.messagesCount}
            loading={isLoading}
          />
        </div>
      </section>

      {/* ── Recent platform activity ────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recent platform activity</CardTitle>
          <CardDescription>
            Last 20 platform-audit entries. Per-tenant audit lives on each
            tenant's detail page.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Sysadmin</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Target tenant</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-muted-foreground py-8"
                  >
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                data?.recentPlatformActivity.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-muted-foreground py-8"
                    >
                      No platform activity yet.
                    </TableCell>
                  </TableRow>
                )}
              {data?.recentPlatformActivity.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatRelative(new Date(row.createdAt))}
                  </TableCell>
                  <TableCell className="text-xs font-medium">
                    {row.sysadminEmail}
                  </TableCell>
                  <TableCell className="text-xs">
                    <Badge variant="outline" className="font-mono text-[11px]">
                      {row.action}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {row.category}
                  </TableCell>
                  <TableCell className="text-xs">
                    {row.targetTenantId ? (
                      <Link
                        to="/sysadmin/tenants/$tenantId"
                        params={{ tenantId: row.targetTenantId }}
                        className="text-primary hover:underline inline-flex items-center gap-1 font-mono text-[11px]"
                      >
                        {row.targetTenantId.slice(0, 8)}…
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Section heading ────────────────────────────────────────────────────────

function SectionHeading({
  icon,
  title,
  subtitle,
  link,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  link?: { to: string; label: string };
}) {
  return (
    <div className="flex items-end justify-between">
      <div>
        <div className="text-sm font-medium flex items-center gap-2">
          {icon}
          {title}
        </div>
        {subtitle && (
          <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>
        )}
      </div>
      {link && (
        <Link
          to={link.to}
          className="text-xs text-primary inline-flex items-center gap-1 hover:underline"
        >
          {link.label}
          <ArrowRight className="h-3 w-3" />
        </Link>
      )}
    </div>
  );
}

// ─── Stat card ──────────────────────────────────────────────────────────────

type StatTone = 'default' | 'muted' | 'warning' | 'destructive';

function StatCard({
  label,
  value,
  loading,
  tone = 'default',
  hint,
}: {
  label: string;
  value?: number;
  loading?: boolean;
  tone?: StatTone;
  hint?: string;
}) {
  const valueColor =
    tone === 'warning'
      ? 'text-amber-600'
      : tone === 'destructive'
        ? 'text-destructive'
        : tone === 'muted'
          ? 'text-muted-foreground'
          : '';
  return (
    <Card>
      <CardContent className="py-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className={`text-2xl font-semibold mt-1 ${valueColor}`}>
          {loading ? '—' : (value ?? 0).toLocaleString()}
        </div>
        {hint && (
          <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Alert banner ───────────────────────────────────────────────────────────

function AlertBanner({ alert }: { alert: PlatformAlert }) {
  const Icon =
    alert.severity === 'critical'
      ? AlertCircle
      : alert.severity === 'warning'
        ? AlertTriangle
        : Info;
  const tone =
    alert.severity === 'critical'
      ? 'border-destructive/50 bg-destructive/5 text-destructive'
      : alert.severity === 'warning'
        ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200'
        : 'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-900 dark:bg-blue-900/20 dark:text-blue-200';
  // Each alert links to the surface most likely to resolve it.
  const link =
    alert.code === 'SYSADMIN_TOTP_PENDING'
      ? { to: '/sysadmin/admins', label: 'Review sysadmins' }
      : alert.code === 'TENANT_PENDING_PROVISIONING'
        ? { to: '/sysadmin/tenants', label: 'Review tenants' }
        : alert.code === 'SOFT_DELETE_PURGE_DUE_SOON'
          ? { to: '/sysadmin/tenants', label: 'Review deleted tenants' }
          : null;

  return (
    <div className={`border rounded-md px-4 py-3 flex items-start gap-3 ${tone}`}>
      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="flex-1 text-sm">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] font-mono">
            {alert.code}
          </Badge>
          {typeof alert.count === 'number' && (
            <Badge variant="secondary" className="text-[10px]">
              {alert.count}
            </Badge>
          )}
        </div>
        <div className="mt-1">{alert.message}</div>
      </div>
      {link && (
        <Link
          to={link.to}
          className="text-xs underline inline-flex items-center gap-1 shrink-0"
        >
          {link.label}
          <ArrowRight className="h-3 w-3" />
        </Link>
      )}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatRelative(date: Date): string {
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}
